<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from "vue";
import { onClickOutside } from "@vueuse/core";
import { useAnalytics } from "~/composables/useAnalytics";

const props = withDefaults(
  defineProps<{
    show?: boolean;
    title?: string;
    description?: string;
    ctaHref?: string;
    secondsLeft?: number;
    duration?: number;
  }>(),
  {
    show: false,
    title: "Looking for some inspiration?",
    description: "See what others have been selecting!",
    ctaHref: "/selections?tab=community",
    secondsLeft: undefined,
    duration: 10,
  }
);

const emit = defineEmits<{
  (e: "dismiss"): void;
  (e: "accept"): void;
}>();

const { trackSectionViewed, trackCardClick, generateExposureId } = useAnalytics();
const containerRef = ref<HTMLElement | null>(null);
const internalSeconds = ref(props.duration);
let internalTimer: ReturnType<typeof setInterval> | null = null;

const RADIUS = 9;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const displaySeconds = computed(() => {
  if (props.secondsLeft !== undefined) {
    return props.secondsLeft;
  }
  return internalSeconds.value;
});

const strokeDashoffset = computed(() => {
  const total = props.duration || 10;
  const current = displaySeconds.value;
  const progress = Math.max(0, Math.min(1, current / total));
  return CIRCUMFERENCE * (1 - progress);
});

function startInternalTimer() {
  stopInternalTimer();
  internalSeconds.value = props.duration;
  if (!import.meta.client) return;
  internalTimer = setInterval(() => {
    if (internalSeconds.value > 1) {
      internalSeconds.value--;
    } else {
      internalSeconds.value = 0;
      stopInternalTimer();
      emit("dismiss");
    }
  }, 1000);
}

function stopInternalTimer() {
  if (internalTimer) {
    clearInterval(internalTimer);
    internalTimer = null;
  }
}

watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      try {
        trackSectionViewed({
          section: "selections_coach_mark",
          entity_type: "CTA",
        });
      } catch (_) {}
      if (props.secondsLeft === undefined) {
        startInternalTimer();
      }
    } else {
      stopInternalTimer();
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  stopInternalTimer();
});

onClickOutside(containerRef, (e) => {
  if (props.show && containerRef.value) {
    const target = e.target as HTMLElement | null;
    if (target && target.closest && target.closest(".selections-nav-item")) {
      return;
    }
    emit("dismiss");
  }
});

function handleClick() {
  try {
    trackCardClick({
      entity_type: "CTA",
      entity_id: "selections_coach_mark",
      list_context: "navigation_nudge",
      destination_path: props.ctaHref,
      exposure_id: generateExposureId(),
    });
  } catch (_) {}
  emit("accept");
  navigateTo(props.ctaHref);
}
</script>

<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-2 scale-95"
    enter-to-class="opacity-100 translate-y-0 scale-100"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0 scale-100"
    leave-to-class="opacity-0 translate-y-2 scale-95"
  >
    <div
      v-if="show"
      ref="containerRef"
      class="selections-popover"
      role="dialog"
      aria-label="Explore selections"
      @click="handleClick"
    >
      <!-- Arrow pointing up to the nav link -->
      <span class="selections-popover__arrow" aria-hidden="true" />

      <!-- Close Button with Gray Ring -->
      <button
        type="button"
        class="selections-popover__close"
        aria-label="Dismiss notification"
        title="Dismiss"
        @click.stop.prevent="emit('dismiss')"
      >
        <svg class="selections-popover__ring" viewBox="0 0 22 22" aria-hidden="true">
          <circle
            class="selections-popover__ring-track"
            cx="11"
            cy="11"
            :r="RADIUS"
            fill="none"
            stroke-width="1.5"
          />
          <circle
            class="selections-popover__ring-progress"
            cx="11"
            cy="11"
            :r="RADIUS"
            fill="none"
            stroke-width="1.5"
            stroke-linecap="round"
            :stroke-dasharray="CIRCUMFERENCE"
            :stroke-dashoffset="strokeDashoffset"
            transform="rotate(-90 11 11)"
          />
        </svg>
        <Icon name="lucide:x" class="selections-popover__close-icon" />
      </button>

      <!-- Title & Body -->
      <p class="selections-popover__title">{{ title }}</p>
      <p class="selections-popover__body">{{ description }}</p>
    </div>
  </Transition>
</template>

<style scoped>
.selections-popover {
  position: absolute;
  top: calc(100% + 14px);
  left: 50%;
  transform: translateX(-50%);
  width: 295px;
  background: #1a1a1a;
  color: #ffffff;
  border-radius: 12px;
  padding: 16px 40px 18px 18px;
  z-index: 200;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.32);
  cursor: pointer;
  text-align: left;
  box-sizing: border-box;
}

.selections-popover__arrow {
  position: absolute;
  top: -6px;
  left: 50%;
  width: 12px;
  height: 12px;
  background: #1a1a1a;
  border-radius: 2px;
  transform: translateX(-50%) rotate(45deg);
}

.selections-popover__close {
  position: absolute;
  top: 13px;
  right: 13px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 0;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: color 0.15s ease, background-color 0.15s ease, transform 0.15s ease;
  font-family: inherit;
}

.selections-popover__close:hover {
  color: #ffffff;
  background-color: rgba(255, 255, 255, 0.12);
  transform: scale(1.06);
}

.selections-popover__ring {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.selections-popover__ring-track {
  stroke: rgba(255, 255, 255, 0.15);
}

.selections-popover__ring-progress {
  stroke: rgba(255, 255, 255, 0.5);
  transition: stroke-dashoffset 1s linear;
}

.selections-popover__close-icon {
  position: relative;
  z-index: 1;
  width: 11px;
  height: 11px;
  color: rgba(255, 255, 255, 0.75);
  transition: color 0.15s ease;
}

.selections-popover__close:hover .selections-popover__close-icon {
  color: #ffffff;
}

.selections-popover__title {
  margin: 0 0 5px;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.35;
  color: #ffffff;
}

.selections-popover__body {
  margin: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
}
</style>
